<template>
    <base-box
        v-bind="$props"
        border-radius="large"
        border
        :border-color="cardBorderColor"
        :class="classes"
        v-on="$listeners"
        @click="handleBoxClick"
    >
        <div slot="content">
            <g-level vertical-align="top" class="flex-fix">
                <g-level-item v-if="!isBriefSubmitted" class="flex-fix">
                    <g-icon :name="sideIcon" :class="sideIconColor" :type="sideIconType" />
                </g-level-item>
                <g-level-item class="flex-fix">
                    <div>
                        <g-typography tag="h4" :color="titleFontColor" font-weight="semibold" :value="title" class="g-mb-3" />
                        <g-typography tag="h5" :color="subTitleFontColor" font-weight="normal" :value="subTitle" />
                        <div :class="{ 'g-mb-1' : isComplete }">
                            <g-typography
                                v-if="isError"
                                tag="h5"
                                color="error"
                                font-weight="normal"
                                :value="errorMessage" />
                        </div>
                    </div>
                </g-level-item>
                <g-level-item slot="right">
                    <g-button v-if="isBriefAccepted && mustDeliver" size="large" shape="round" theme="live" type="primary">
                        Deliver
                    </g-button>
                    <g-button
                        v-else-if="!isBriefEditable && !hideViewButton"
                        size="large"
                        shape="round"
                        theme="live"
                        type="default"
                        :route="route"
                    >
                        View
                    </g-button>
                    <g-button
                        v-else-if="isBriefEditable"
                        shape="circle"
                        size="large"
                        :icon="rightIcon"
                        theme="live"
                        :route="route"
                        :disabled="isDisabled" />
                </g-level-item>
            </g-level>
            <div :style="{color: subTitleFontColor}">
                <slot name="details" />
            </div>
        </div>
    </base-box>
</template>

<script>
    import {get} from 'lodash';
    import BaseBox from '../../../library/BaseBox';
    import * as STEP_STATES from '../../../../constants/activation-brief-step-states';
    import {STEP_ERROR_MESSAGES} from '../../../../constants/activation-brief-step-errors';
    import * as STEP_NAMES from '../../../../constants/activation-brief-steps';
    import STEP_ROUTES from '../../../../constants/activation-brief-step-routes';
    import {SIDE_ICON_NAMES, SIDE_ICON_TYPES} from '../../../../constants/activation-brief-steps';
    import {
        ACTIVATION_BRIEF_STATUS_NONE,
        ACTIVATION_BRIEF_STATUS_PENDING_APPROVAL,
    } from '../../../../constants/activation-brief';

    export default {
        name:       'GenericStepCard',
        extends:    BaseBox,
        components: {
            BaseBox,
        },
        props: {
            title: {
                type:     String,
                required: true,
            },
            subTitle: {
                type:    String,
                default: '',
            },
            isPreview: {
                type:    Boolean,
                default: false,
            },
            step: {
                type:     Object,
                required: true,
            },
            currentStepName: {
                type:    String,
                default: '',
            },
            isBriefAccepted: {
                type:    Boolean,
                default: false,
            },
            brief: {
                type:     Object,
                required: true,
            },
            contact: {
                type: Object,
            },
            hideViewButton: {
                type:    Boolean,
                default: false,
            },
            isBriefEditable: {
                type: Boolean,
            },
            setRoute: {
                type: String,
                default: null,
            }
        },
        computed: {
            stepName() {
                return this.step.name;
            },
            route() {
                if (this.setRoute) {
                    return this.setRoute;
                }
                if (STEP_ROUTES.hasOwnProperty(this.stepName)) {
                    return {name: STEP_ROUTES[this.stepName]};
                }

                return '/';
            },
            titleFontColor() {
                return this.isCurrent ? 'white' : 'normal';
            },
            subTitleFontColor() {
                return this.isCurrent ? 'white' : 'secondary';
            },
            cardBorderColor() {
                if (this.isError) {
                    return 'red';
                }
                return 'default';
            },
            rightIcon() {
                return this.state === 'completed' ? 'pencil' : 'arrow-right';
            },
            sideIcon() {
                switch (this.state) {
                case STEP_STATES.COMPLETED:
                    return 'check-circle';
                case STEP_STATES.ERROR:
                    return 'exclamation-circle';
                default:
                    if (SIDE_ICON_NAMES.hasOwnProperty(this.stepName)) {
                        return SIDE_ICON_NAMES[this.stepName];
                    } else {
                        return 'question-circle';
                    }
                }
            },
            sideIconColor() {
                return `side-icon-color-${this.state}`;
            },
            sideIconType() {
                if (SIDE_ICON_TYPES.hasOwnProperty(this.state)) {
                    return SIDE_ICON_TYPES[this.state];
                }
                return 'light';
            },
            isDisabled() {
                return this.state === 'pending' && !this.isPreview;
            },
            isError(){
                return this.state === STEP_STATES.ERROR;
            },
            isCurrent(){
                return this.state === STEP_STATES.CURRENT;
            },
            isComplete() {
                return this.state === STEP_STATES.COMPLETED;
            },
            isPending() {
                return this.state === STEP_STATES.PENDING;
            },
            isStarted() {
                return this.step.is_started;
            },
            state() {
                if (this.step.is_complete || this.isBriefAccepted) {
                    return STEP_STATES.COMPLETED;
                }

                if (!this.step && !this.isBriefAccepted) {
                    return STEP_STATES.PENDING;
                }

                if (this.step.is_started && (!this.step.is_complete || !this.step.is_valid)) {
                    return STEP_STATES.ERROR;
                }

                if (this.stepName === this.currentStepName && !this.isBriefAccepted) {
                    return STEP_STATES.CURRENT;
                }

                return STEP_STATES.PENDING;
            },
            errorMessage() {
                if (!this.step.error) {
                    return '';
                }

                if (STEP_ERROR_MESSAGES[this.step.name] && STEP_ERROR_MESSAGES[this.step.name][this.step.error]) {
                    return STEP_ERROR_MESSAGES[this.step.name][this.step.error];
                } else {
                    return this.step.error.replace(/_/g, ' ');
                }
            },
            isTasksStep() {
                return this.stepName === STEP_NAMES.NAME_TASKS;
            },
            mustDeliver() {
                return this.isTasksStep && get(this.brief, 'module_progress', []) && !get(this.brief, 'has_module_creator_licensing', false);
            },
            isBriefSubmitted() {
                return this.brief.status !== ACTIVATION_BRIEF_STATUS_NONE && this.brief.status !== ACTIVATION_BRIEF_STATUS_PENDING_APPROVAL;
            },
            classes() {
                return {
                    'g-p-6':                      true,
                    [`background-color-${this.state}`]: true,
                    'step-card-current':                this.isCurrent,
                };
            },
        },
        methods: {
            handleBoxClick() {
                if (!this.isDisabled && this.route) {
                    this.$router.push(this.route);
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    ::v-deep .flex-fix .g-level-left{
        justify-content: flex-start;
        align-items: flex-start;
     }
    .background-color-pending {
        background-color: $g-color-grey-3;
    }
    .background-color-current {
        background-color: $g-color-grey-7;
    }
    .background-color-error {
        background-color: $g-color-white;
    }
    .background-color-completed {
        background-color: $g-color-white;
    }
    .side-icon-color-pending {
        color: $g-color-grey-5;
    }
    .side-icon-color-current {
        color: $g-color-white;
    }
    .side-icon-color-completed {
        color: $g-color-green-5;
    }
    .side-icon-color-error {
        color: $g-color-red-5;
    }
</style>
